import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Container,
	Box,
	Flex,
	Text,
	Center,
	Badge,
	useToast,
	Button,
	useDisclosure,
	Avatar,
	WrapItem,
	Wrap,
	Tooltip,
	SimpleGrid,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	Textarea,
	ModalFooter,
	AvatarBadge,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
} from '@chakra-ui/react';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import { faCheck, faTimes, faPrint, faPlusSquare, faMinusSquare, faPlus, faBackward, faBackspace, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from '../../constant';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import useStore from '../../store';
import PrintLayout from '../../components/Print/PrintLayout';
import LabelDetail from '../../components/LabelDetail';
import DownloadAndViewButton from '../../components/Files/DownloadAndViewButton';
import UserDislayNameById from '../../components/UserDislayNameById';
import { AddIcon, CheckIcon, CloseIcon, DeleteIcon, QuestionIcon } from '@chakra-ui/icons';
import getPositionLabel from '../../utils/getPositionLabel';
import ViewComment from '../../components/Comment/ViewComment';
import SelectUserAllModal from '../../components/Modal/SelectUserShowAllModal';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const CATEGORY_LABELS = {
	RequestForMeetingParticipation: 'Request for Meeting Participation (Phnom Penh)',
	Mission: 'Mission (Domestic and International)',
	AttendanceRequest: 'Attendance Request: Forgot to Check-in/Check-out',
};

const QUERY_DETAIL = gql`
	query get($id: String!) {
		getMissionRecordById(id: $id) {
			id
			category
			no
			reason
			createdBy
			createdAt
			amountOfDays
			fromDate
			toDate
			attachmentFile
			reviewingBy
			status
			step
			user {
				id
				username
				fullName
				fullNameKhmer
				department
				position
			}
			viewers {
				id
				createdAt
				createdBy
				viewerType
				acceptByParticipant
				acceptByParticipantBy
				acceptByParticipantAt
				approveByOffice
				approveByOfficeBy
				approveByOfficeAt
				approveByDepartment
				approveByDepartmentBy
				approveByDepartmentAt
				acknowledgeByHr
				acknowledgeByHrBy
				acknowledgeByHrAt
				approveByDirectorGeneral
				approveByDirectorGeneralBy
				approveByDirectorGeneralAt
				rejectReason
				viewer {
					id
					username
					fullName
					fullNameKhmer
					avatar
					department
					position
					office
					canApproveMissionRecord
				}
			}
			notification {
				id
				read
			}
		}
	}
`;

const APPROVE_MUTATION = gql`
	mutation approve($id: String!) {
		approveMissionRecord(id: $id)
	}
`;

const REJECT_MUTATION = gql`
	mutation reject($id: String!, $reason: String!) {
		rejectMissionRecord(id: $id, reason: $reason)
	}
`;

const MUTATION_READ_NOTIFICATION = gql`
	mutation read($id: String!) {
		readNotificationById(id: $id)
	}
`;

const MUTATION_ADD_PARTICIPANT = gql`
	mutation addMissionRecordParticipant($id: String!, $participantId: String!) {
		addMissionRecordParticipant(id: $id, participantId: $participantId)
	}
`;

const MUTATION_REMOVE_PARTICIPANT = gql`
	mutation removeMissionRecordParticipant($id: String!, $participantId: String!) {
		removeMissionRecordParticipant(id: $id, participantId: $participantId)
	}
`;

const MUTATION_REMOVE_ATTACHMENT = gql`
	mutation removeMissionRecordAttachment($id: String!) {
		removeMissionRecordAttachment(id: $id)
	}
`;

const MUTATION_UPDATE_ATTACHMENT = gql`
	mutation updateMissionRecordAttachment($id: String!, $attachmentFile: Upload!, $attachmentFileSize: Float) {
		updateMissionRecordAttachment(id: $id, attachmentFile: $attachmentFile, attachmentFileSize: $attachmentFileSize)
	}
`;

const LABEL_WIDTH = '160px';

function MissionRecordDetail() {
	const { currentLang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { countUnreadNotifications } = useStore((state) => state.countUnreadNotifications);
	const setCountUnreadNotifications = useStore((state) => state.setCountUnreadNotifications);
	moment.locale(currentLang === 'kh' ? 'km' : 'en-gb');
	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const id = urlParams.get('id') ? urlParams.get('id') : null;
	const toast = useToast();
	const [record, setRecord] = useState(null);
	const [rejectReason, setRejectReason] = useState('');
	const [showApproveButton, setShowApproveButton] = useState(false);
	const [noDataFound, setNoDataFound] = useState(false);
	const cancelRef = useRef();
	const [participantToRemove, setParticipantToRemove] = useState(null);
	const [openSelectUserAllModel, setOpenSelectUserAllModel] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isAttachmentModalOpen, onOpen: onAttachmentModalOpen, onClose: onAttachmentModalClose } = useDisclosure();
	const { isOpen: isRemoveUserModalOpen, onOpen: onRemoveUserModalOpen, onClose: onRemoveUserModalClose } = useDisclosure();
	const [loadDetail, { data, loading, error }] = useLazyQuery(QUERY_DETAIL, {
		fetchPolicy: 'no-cache',
		onCompleted: (v) => {
			const missionRecord = v?.getMissionRecordById;
			if (!missionRecord?.id) {
				setRecord(null);
				setNoDataFound(true);
				return;
			}

			setNoDataFound(false);
			setRecord(missionRecord);
		},
		onError: () => {
			setRecord(null);
			setNoDataFound(true);
		},
	});
	const [approveMissionRecord, { loading: loadingApproveMissionRecord, data: dataApproveMissionRecord }] = useMutation(APPROVE_MUTATION);
	const [rejectMissionRecord, { loading: loadingRejectMissionRecord, data: dataRejectMissionRecord }] = useMutation(REJECT_MUTATION);
	const [readNotification, {}] = useMutation(MUTATION_READ_NOTIFICATION);
	const [addParticipant, { loading: loadingAddParticipant }] = useMutation(MUTATION_ADD_PARTICIPANT);
	const [removeParticipant, { loading: loadingRemoveParticipant }] = useMutation(MUTATION_REMOVE_PARTICIPANT);
	const [removeAttachment, { loading: loadingRemoveAttachment }] = useMutation(MUTATION_REMOVE_ATTACHMENT);
	const [updateAttachment, { loading: loadingUpdateAttachment }] = useMutation(MUTATION_UPDATE_ATTACHMENT);

	useEffect(getDetail, [id]);

	function getDetail() {
		id && loadDetail({ variables: { id } });
	}

	useEffect(() => {
		if (!user) return;

		const showButton = user?.canApproveMissionRecord === true || user?.role?.includes('admin') || user?.position === '4' || user?.position === '1';

		setShowApproveButton(showButton);
	}, [user]);

	useEffect(() => {
		if (record?.notification?.read === false) {
			setCountUnreadNotifications(countUnreadNotifications - 1);
			readNotification({ variables: { id: record?.notification?.id } });
		}
	}, [record]);

	useEffect(() => {
		if (dataApproveMissionRecord?.approveMissionRecord) {
			toast({
				title: t('Mission Record has been approved successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
				duration: 3000,
			});
			loadDetail({
				variables: {
					id,
				},
			});
		}
	}, [dataApproveMissionRecord]);

	useEffect(() => {
		if (dataRejectMissionRecord?.rejectMissionRecord) {
			toast({
				title: t('Mission record has been rejected'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
				duration: 3000,
			});
			loadDetail({
				variables: {
					id,
				},
			});
			onClose();
		}
	}, [dataRejectMissionRecord]);

	const normalizeViewerType = (type) => String(type || '').toUpperCase();

	const isParticipantViewer = (v) => normalizeViewerType(v?.viewerType) === 'PARTICIPANT';

	const isOfficeViewer = (v) => normalizeViewerType(v?.viewerType) === 'OFFICE';

	const isDepartmentViewer = (v) => normalizeViewerType(v?.viewerType) === 'DEPARTMENT';

	const isHrViewer = (v) => normalizeViewerType(v?.viewerType) === 'HR';

	const isHrCreator = record?.user?.office === 'human_resource' || (record?.createdBy === user?.id && user?.office === 'human_resource');

	const isHrAccount = user?.office === 'human_resource';

	const isDirectorGeneralViewer = (v) => {
		const type = normalizeViewerType(v?.viewerType);
		return type === 'DG' || type === 'DIRECTORGENERAL' || type === 'DIRECTOR_GENERAL';
	};

	const canActionViewer = (item, step) => {
		if (!record) return false;

		const isPending = record?.status === 'INPROGRESS' || record?.status === null;

		if (!isPending) return false;

		if (record?.step !== step) return false;

		const isSameUser = String(item?.viewer?.id) === String(user?.id);
		const isAdmin = user?.role === 'admin' || user?.role?.includes?.('admin');

		if (!isSameUser && !isAdmin) return false;

		if (step === 'Participant') {
			if (!isParticipantViewer(item)) return false;
			return item?.acceptByParticipant === null;
		}

		if (step === 'Office') {
			if (!isOfficeViewer(item)) return false;

			const position = String(item?.viewer?.position);
			const isOfficeApprover = item?.viewer?.canApproveMissionRecord === true && ['5', '6'].includes(position);

			return item?.approveByOffice === null && (isOfficeApprover || isAdmin);
		}

		if (step === 'Department') {
			if (!isDepartmentViewer(item)) return false;

			const position = String(item?.viewer?.position);
			const isDepartmentApprover = item?.viewer?.canApproveMissionRecord === true && ['2', '3', '4'].includes(position);

			return item?.approveByDepartment === null && (isDepartmentApprover || isAdmin);
		}

		if (step === 'HR') {
			if (!isHrViewer(item)) return false;

			const isHrApprover = item?.viewer?.canApproveMissionRecord === true && item?.viewer?.office === 'human_resource';
			const isSameUser = String(item?.viewer?.id) === String(user?.id);

			return isSameUser && isHrApprover && item?.acknowledgeByHr === null;
		}

		if (step === 'DirectorGeneral') {
			if (!isDirectorGeneralViewer(item)) return false;
			return String(record?.reviewingBy) === String(user?.id) && item?.approveByDirectorGeneral === null;
		}

		return false;
	};

	const isHrApproverViewer = (v) => {
		return v?.viewer?.canApproveMissionRecord === true && v?.viewer?.office === 'human_resource';
	};

	const admin = user?.role === 'admin';
	const currentViewer = record?.viewers?.find((v) => String(v?.viewer?.id) === String(user?.id));
	const canExportMissionRecordData = user?.canExportMissionRecordData === true;
	const isCreator = String(record?.createdBy) === String(user?.id);

	// Participant
	const participantViewers = record?.viewers?.filter((v) => isParticipantViewer(v)) || [];

	const isCurrentUserOfficeApprover =
		record?.viewers?.some((v) => {
			return (
				isOfficeViewer(v) &&
				String(v?.viewer?.id) === String(user?.id) &&
				v?.viewer?.canApproveMissionRecord === true &&
				['5', '6'].includes(String(v?.viewer?.position))
			);
		}) || false;

	const lastEditableTime = record?.fromDate ? moment(record?.fromDate).startOf('day').add(2, 'days') : null;

	const isWithinEditablePeriod = record?.status === 'APPROVED' && lastEditableTime !== null && moment().isSameOrBefore(lastEditableTime);

	const isDepartmentAccount = user?.canApproveMissionRecord === true && ['2', '3', '4'].includes(String(user?.position)) && user?.office !== 'human_resource';

	const isCurrentUserParticipant = participantViewers?.some((v) => String(v?.viewer?.id) === String(user?.id)) || false;

	const isDG = String(user?.position) === '1';

	const canManageParticipant = (() => {
		if (!record) return false;

		if (record?.status === 'REJECTED') return false;

		// DG account cannot add participant
		if (isDG) return false;

		if (isHrAccount && isCurrentUserParticipant) return false;

		if (admin) return true;

		if (isHrAccount && isCreator && record?.status !== 'APPROVED') {
			return true;
		}

		if (record?.status === 'APPROVED') {
			return isDepartmentAccount && isWithinEditablePeriod;
		}

		if (isDepartmentAccount && ['Department', 'HR', 'DirectorGeneral'].includes(record?.step) && record?.category !== 'AttendanceRequest') {
			return true;
		}

		if (record?.step === 'Office') {
			return isCurrentUserOfficeApprover;
		}

		return false;
	})();

	const canRemoveParticipant = canManageParticipant && participantViewers?.length > 1;

	const isCreatorRemovingSelf = (participant) => {
		return String(record?.createdBy) === String(user?.id) && String(participant?.viewer?.id) === String(user?.id);
	};

	const isCurrentUserParticipantProfile = (participant) => {
		return String(participant?.viewer?.id) === String(user?.id);
	};

	// Office
	const officeViewers = record?.viewers?.filter((v) => isOfficeViewer(v)) || [];

	const showOfficeNABadge = record?.category === 'RequestForMeetingParticipation' && officeViewers.length === 0;

	// Department
	const departmentApprovedViewers = record?.viewers?.filter((v) => isDepartmentViewer(v) && v?.approveByDepartment === true) || [];

	const departmentRejectedViewers = record?.viewers?.filter((v) => isDepartmentViewer(v) && v?.approveByDepartment === false) || [];

	const pendingDepartmentViewers =
		record?.viewers?.filter((v) => {
			return (
				isDepartmentViewer(v) &&
				record?.step === 'Department' &&
				v?.viewer?.canApproveMissionRecord === true &&
				['2', '3', '4'].includes(String(v?.viewer?.position)) &&
				v?.viewer?.office !== 'human_resource' &&
				v?.approveByDepartment === null
			);
		}) || [];

	const departmentViewers =
		departmentApprovedViewers?.length > 0
			? departmentApprovedViewers
			: departmentRejectedViewers?.length > 0
				? departmentRejectedViewers
				: pendingDepartmentViewers;

	const creatorViewer = record?.viewers?.find((v) => String(v?.viewer?.id) === String(record?.createdBy));

	const shouldShowDepartmentSection = ['RequestForMeetingParticipation', 'AttendanceRequest'].includes(record?.category);

	const isCreatedByDepartmentUser =
		record?.category === 'RequestForMeetingParticipation' &&
		creatorViewer?.viewer?.canApproveMissionRecord === true &&
		['2', '3', '4'].includes(String(creatorViewer?.viewer?.position)) &&
		creatorViewer?.viewer?.office !== 'human_resource';

	const showDepartmentNABadge = shouldShowDepartmentSection && departmentViewers?.length === 0;

	// DG
	const dgViewer =
		record?.viewers?.filter((v) => {
			const isDGViewerType = isDirectorGeneralViewer(v);
			const isDGPosition = String(v?.viewer?.position) === '1';

			const isPendingDG =
				record?.step === 'DirectorGeneral' && String(v?.viewer?.id) === String(record?.reviewingBy) && v?.approveByDirectorGeneral === null;

			const isCompletedDG = v?.approveByDirectorGeneral === true || v?.approveByDirectorGeneral === false;

			return record?.category === 'AttendanceRequest' && isDGViewerType && isDGPosition && (isPendingDG || isCompletedDG);
		}) || [];

	// HR
	const isHrApprover = user?.office === 'human_resource' && user?.canApproveMissionRecord === true;

	const hrAcknowledgedViewers = record?.viewers?.filter((v) => isHrViewer(v) && v?.acknowledgeByHr === true) || [];

	const hrRejectedViewers = record?.viewers?.filter((v) => isHrViewer(v) && v?.acknowledgeByHr === false) || [];

	const pendingHrViewers =
		record?.viewers?.filter((v) => {
			return record?.step === 'HR' && isHrViewer(v) && isHrApproverViewer(v) && v?.acknowledgeByHr === null;
		}) || [];

	const hrViewers = hrAcknowledgedViewers.length > 0 ? hrAcknowledgedViewers : hrRejectedViewers.length > 0 ? hrRejectedViewers : pendingHrViewers;

	const shouldShowParticipantSection = (currentViewer || isCreator || isHrApprover || admin || canExportMissionRecordData) && !isCreatedByDepartmentUser;

	const visibleParticipantViewers = participantViewers;

	if (loading) {
		return (
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: 'Mission Record',
							path: `/mission-record/list`,
						},
						{
							name: 'Detail',
							path: '#',
						},
					]}
				/>

				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						p="32px"
					>
						<Center minH="200px">
							<Text fontSize="lg" fontWeight="bold" color="gray.500">
								{t('Loading...')}
							</Text>
						</Center>
					</Box>
				</Center>
			</Container>
		);
	}

	if (noDataFound || !record) {
		return (
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: 'Mission Record',
							path: `/mission-record/list`,
						},
						{
							name: 'Detail',
							path: '#',
						},
					]}
				/>

				<Flex mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						p="32px"
					>
						<Center minH="100px">
							<Text fontSize="lg" fontWeight="bold" color="gray.500">
								{t('No data found')}
							</Text>
						</Center>
						<Flex direction="column" align="center">
							<Button
								w={currentLang === 'kh' ? '20%' : '15%'}
								leftIcon={<FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 16 }} />}
								onClick={() => history.push(`/mission-record/list`)}
								variant="solid"
								size="md"
								h="42px"
								borderRadius={c.borderRadius}
								color="#1284d0"
							>
								{t('Return to List')}
							</Button>
						</Flex>
					</Box>
				</Flex>
			</Container>
		);
	}

	return (
		<>
			<PrintLayout>
				<>
					<Container maxW="container.xl" mt="16px" mb="16px">
						<BreadcrumbComponent
							list={[
								{
									name: 'Mission Record',
									path: `/mission-record/list`,
								},
								{
									name: 'Detail',
									path: '#',
								},
							]}
						/>
						<Center mt="5">
							<Box
								w="100%"
								bg="white"
								borderRadius={c.borderRadius}
								border="1px solid #dbdbdb"
								boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
								pb="32px"
							>
								<Flex
									alignItems="center"
									justifyContent="space-between"
									bg="#FAFAFA"
									p="16px"
									pt="8px"
									pb="8px"
									mb="16px"
									borderBottom="1px solid #dbdbdb"
								>
									<Flex justifyContent="center" alignItems="center">
										<Text fontSize="x-large" fontWeight="bold" mr="4">
											{t('Detail')}
										</Text>
									</Flex>

									<Flex className="hidden-print">
										<Button
											onClick={() => window?.print()}
											leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Print')}
										</Button>
									</Flex>
								</Flex>

								<Box p="16px">
									<LabelDetail
										mb={1}
										mt={0}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Number"
										titleDetail={record?.no ? String(record?.no).padStart(4, '0') : 'N/A'}
									/>
									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Category"
										titleDetail={CATEGORY_LABELS[record?.category] || record?.category}
									/>
									<LabelDetail mb={1} mt={2} labelWidth={LABEL_WIDTH} direction="row" label="Reason" titleDetail={record?.reason} />
									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Amount of Days"
										titleDetail={record?.amountOfDays}
									/>
									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Date From"
										titleDetail={record?.fromDate ? moment(record.fromDate).format('LLLL') : 'N/A'}
									/>
									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Date To"
										titleDetail={record?.toDate ? moment(record.toDate).format('LLLL') : 'N/A'}
									/>

									<Flex alignItems="center" mt={1} pt={1} pb={1}>
										<Text fontWeight="semibold" minW="175px" w="175px">
											{t('Attachment File')}
										</Text>

										{record?.attachmentFile ? (
											<Flex className="viewContainer" alignItems="center" mt="2">
												<DownloadAndViewButton
													minW="150px"
													url={record?.attachmentFile?.file}
													size={record?.attachmentFile?.size}
													marginLeft={0}
												/>

												{record?.status === 'REJECTED'
													? null
													: isCreator &&
														record?.status !== 'APPROVED' && (
															<FontAwesomeIcon
																onClick={onAttachmentModalOpen}
																className="remove"
																icon={faMinusSquare}
																style={{
																	fontSize: 18,
																	display: 'none',
																	position: 'absolute',
																	zIndex: '100',
																	marginLeft: 135,
																	marginBottom: 30,
																	color: 'red',
																	backgroundColor: 'white',
																	padding: 2,
																	cursor: 'pointer',
																}}
															/>
														)}
											</Flex>
										) : (
											<Flex alignItems="center">
												{(!isCreator || record?.status === 'REJECTED') && <Text>N/A</Text>}
												{record?.status === 'REJECTED' ? null : isCreator && record?.status !== 'APPROVED' ? (
													<Button
														as="label"
														size="sm"
														colorScheme="blue"
														rightIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 18 }} />}
														borderRadius={c.borderRadius}
														isLoading={loadingUpdateAttachment}
													>
														{t('Add Attachment File')}
														<input
															type="file"
															hidden
															onChange={async (e) => {
																const attachmentFile = e.target.files?.[0];

																if (!attachmentFile) return;

																await updateAttachment({
																	variables: {
																		id,
																		attachmentFile,
																		attachmentFileSize: Number(attachmentFile?.size),
																	},
																});

																toast({
																	title: t('Attachment uploaded successfully'),
																	status: 'success',
																	isClosable: true,
																	position: 'top-right',
																	duration: 3000,
																});

																getDetail();
																e.target.value = '';
															}}
														/>
													</Button>
												) : null}

												{isCreator && record?.status === 'APPROVED' && <Text>N/A</Text>}
											</Flex>
										)}
									</Flex>

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Status"
										childDetail={
											record?.status === 'APPROVED' ? (
												<Text color="green" fontSize="15px" fontWeight="bold">
													{t('mrAPPROVED')}
												</Text>
											) : record?.status === 'REJECTED' ? (
												<Text color="red" fontSize="15px" fontWeight="bold">
													{t('mrREJECTED')}
												</Text>
											) : record?.category === 'RequestForMeetingParticipation' ||
											  record?.category === 'Mission' ||
											  record?.category === 'AttendanceRequest' ? (
												<Text color="blue" fontSize="15px" fontWeight="bold">
													{record?.step === 'Participant'
														? t('Waiting for participants to accept')
														: record?.step === 'Office'
															? t('Waiting for division to approve')
															: record?.step === 'Department'
																? t('Waiting for department to approve')
																: record?.step === 'HR'
																	? t("Waiting for HR's acknowledgement")
																	: t('Waiting for DG to approve')}
												</Text>
											) : (
												<Text color="blue" fontSize="15px" fontWeight="bold">
													{t('Waiting for participants to accept')}
												</Text>
											)
										}
									/>

									{record?.category === 'AttendanceRequest' && (
										<Flex mt={4} pt={1} pb={1}>
											<Text fontWeight="bold" minW="200px" w="200px">
												{t('Approved by DG')}
											</Text>
											<Box>{dgViewer?.length === 0 ? <Badge colorScheme="gray">{t('N/A')}</Badge> : null}</Box>
											<Wrap>
												{dgViewer?.map((item, index) => {
													const status =
														item?.approveByDirectorGeneral === true
															? t('mrAPPROVED')
															: item?.approveByDirectorGeneral === false
																? t('mrREJECTED')
																: 'N/A';

													const approvedAt = item?.approveByDirectorGeneralAt
														? moment(item?.approveByDirectorGeneralAt).format('LLLL')
														: null;

													const a = (
														<Box textAlign="center">
															<UserDislayNameById id={item?.viewer?.id} />
															{item?.viewer?.position?.length > 0 ? ', ' : ''}
															{getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
															{', '}
															{t(status)} {t('on')}
															{approvedAt ? ` ${approvedAt}` : ''}
															<br />
															{item?.approveByDirectorGeneral === false ? `${t('Reason')} : ${item?.rejectReason}` : null}
														</Box>
													);

													const b = (
														<Box textAlign="center">
															<UserDislayNameById id={item?.viewer?.id} />
															{', '} {getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
															{', '}
															{t("Waiting for DG's approval")}
														</Box>
													);

													return (
														<>
															<Flex direction="column" justifyContent="center" alignItems="center" key={'d-' + index}>
																<Tooltip label={item?.approveByDirectorGeneral === null ? b : a}>
																	<WrapItem className="avatarWraper">
																		<Avatar
																			name={item?.viewer?.fullNameKhmer}
																			src={
																				item?.viewer?.avatar?.icon
																					? c.baseFilePath + item?.viewer?.avatar?.icon
																					: undefined
																			}
																		>
																			{item?.approveByDirectorGeneral === true ? (
																				<AvatarBadge boxSize="1.25em" bg="green.500">
																					<CheckIcon color="white" boxSize="0.55em" />
																				</AvatarBadge>
																			) : item?.approveByDirectorGeneral === false ? (
																				<AvatarBadge boxSize="1.25em" bg="red.500">
																					<CloseIcon color="white" boxSize="0.55em" />
																				</AvatarBadge>
																			) : (
																				<AvatarBadge boxSize="1.25em" bg="gray">
																					<QuestionIcon color="white" boxSize="12px" />
																				</AvatarBadge>
																			)}
																		</Avatar>
																	</WrapItem>
																</Tooltip>
																<Text textAlign="center" mt={1.5}>
																	{currentLang === 'en' ? item?.viewer?.fullName : item?.viewer?.fullNameKhmer || 'N/A'}
																</Text>
															</Flex>
															<Flex>
																{canActionViewer(item, 'DirectorGeneral') && showApproveButton && (
																	<SimpleGrid columns={2} spacingX={2} mt={2} ml={2}>
																		<Button
																			width="100%"
																			onClick={() => approveMissionRecord({ variables: { id } })}
																			leftIcon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 16 }} />}
																			colorScheme="green"
																			variant="solid"
																			size="sm"
																			borderRadius={c.borderRadius}
																			border="1px solid #bdc3c7"
																			isLoading={loadingApproveMissionRecord}
																		>
																			{t('mrApprove')}
																		</Button>

																		<Button
																			width="100%"
																			onClick={onOpen}
																			leftIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
																			colorScheme="red"
																			variant="solid"
																			size="sm"
																			borderRadius={c.borderRadius}
																			border="1px solid #bdc3c7"
																		>
																			{t('Reject')}
																		</Button>
																	</SimpleGrid>
																)}
															</Flex>
														</>
													);
												})}
											</Wrap>
										</Flex>
									)}

									<Box mt={5}>
										{(record?.category === 'Mission' || record?.category === 'RequestForMeetingParticipation') && (
											<Flex mt={4} pt={1} pb={1}>
												<Text fontWeight="bold" minW="200px" w="200px">
													{t('Acknowledged by HR')}
												</Text>
												<Box>{hrViewers?.length === 0 ? <Badge colorScheme="gray">{t('N/A')}</Badge> : null}</Box>
												<Wrap>
													{[...(hrViewers || [])]
														.sort((a, b) => {
															const aCanAction = canActionViewer(a, 'HR');
															const bCanAction = canActionViewer(b, 'HR');

															if (aCanAction && !bCanAction) return 1;
															if (!aCanAction && bCanAction) return -1;

															return 0;
														})
														.map((item, index) => {
															const status =
																item?.acknowledgeByHr === true
																	? t('ACKNOWLEDGED')
																	: item?.acknowledgeByHr === false
																		? t('REJECTED')
																		: 'N/A';

															const acknowledgedAt = item?.acknowledgeByHrAt
																? moment(item?.acknowledgeByHrAt).format('LLLL')
																: null;
															const a = (
																<Box textAlign="center">
																	<UserDislayNameById id={item?.viewer?.id} />
																	{item?.viewer?.position?.length > 0 ? ', ' : ''}
																	{getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																	{', '}
																	{t(status)} {t('on')}
																	{acknowledgedAt ? ` ${acknowledgedAt}` : ''}
																	<br />
																	{item?.acknowledgeByHr === false ? `${t('Reason')} : ${item?.rejectReason}` : null}
																</Box>
															);

															const b = (
																<Box textAlign="center">
																	<UserDislayNameById id={item?.viewer?.id} />
																	{', '} {getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																	{', '}
																	{t("Waiting for HR's acknowledgement")}
																</Box>
															);

															return (
																<>
																	<Flex direction="column" justifyContent="center" alignItems="center" key={'hr-' + index}>
																		<Tooltip label={item?.acknowledgeByHr === null ? b : a}>
																			<WrapItem className="avatarWraper">
																				<Avatar
																					name={item?.viewer?.fullNameKhmer}
																					src={
																						item?.viewer?.avatar?.icon
																							? c.baseFilePath + item?.viewer?.avatar?.icon
																							: undefined
																					}
																				>
																					{item?.acknowledgeByHr === true ? (
																						<AvatarBadge boxSize="1.25em" bg="green.500">
																							<CheckIcon color="white" boxSize="0.55em" />
																						</AvatarBadge>
																					) : item?.acknowledgeByHr === false ? (
																						<AvatarBadge boxSize="1.25em" bg="red.500">
																							<CloseIcon color="white" boxSize="0.55em" />
																						</AvatarBadge>
																					) : (
																						<AvatarBadge boxSize="1.25em" bg="gray">
																							<QuestionIcon color="white" boxSize="12px" />
																						</AvatarBadge>
																					)}
																				</Avatar>
																			</WrapItem>
																		</Tooltip>

																		<Text textAlign="center" mt={1.5}>
																			{currentLang === 'en'
																				? item?.viewer?.fullName
																				: item?.viewer?.fullNameKhmer || 'N/A'}
																		</Text>
																	</Flex>
																	<Flex>
																		{canActionViewer(item, 'HR') && (
																			<SimpleGrid columns={2} spacingX={2} mt={2} ml={2}>
																				<Button
																					width="100%"
																					onClick={() => approveMissionRecord({ variables: { id } })}
																					leftIcon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 16 }} />}
																					colorScheme="green"
																					variant="solid"
																					size="sm"
																					borderRadius={c.borderRadius}
																					border="1px solid #bdc3c7"
																					isLoading={loadingApproveMissionRecord}
																				>
																					{t('Acknowledge')}
																				</Button>

																				<Button
																					width="100%"
																					onClick={onOpen}
																					leftIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
																					colorScheme="red"
																					variant="solid"
																					size="sm"
																					borderRadius={c.borderRadius}
																					border="1px solid #bdc3c7"
																				>
																					{t('Reject')}
																				</Button>
																			</SimpleGrid>
																		)}
																	</Flex>
																</>
															);
														})}
												</Wrap>
											</Flex>
										)}

										{(record?.category === 'RequestForMeetingParticipation' || record?.category === 'AttendanceRequest') && (
											<Flex mt={4} pt={1} pb={1}>
												<Text fontWeight="bold" minW="200px" w="200px">
													{t('Approved by Department')}
												</Text>
												<Box>{showDepartmentNABadge ? <Badge colorScheme="gray">{t('N/A')}</Badge> : null}</Box>
												<Wrap>
													{departmentViewers?.map((item, index) => {
														const status =
															item?.approveByDepartment === true
																? t('mrAPPROVED')
																: item?.approveByDepartment === false
																	? t('mrREJECTED')
																	: 'N/A';

														const approvedAt = item?.approveByDepartmentAt
															? moment(item?.approveByDepartmentAt).format('LLLL')
															: null;

														const a = (
															<Box textAlign="center">
																<UserDislayNameById id={item?.viewer?.id} />
																{item?.viewer?.position?.length > 0 ? ', ' : ''}
																{getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																{', '}
																{t(status)} {t('on')}
																{approvedAt ? ` ${approvedAt}` : ''}
																<br />
																{item?.approveByDepartment === false ? `${t('Reason')} : ${item?.rejectReason}` : null}
															</Box>
														);

														const b = (
															<Box textAlign="center">
																<UserDislayNameById id={item?.viewer?.id} />
																{', '} {getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																{', '}
																{t("Waiting for Department's approval")}
															</Box>
														);

														return (
															<>
																<Flex direction="column" justifyContent="center" alignItems="center" key={'d-' + index}>
																	<Tooltip label={item?.approveByDepartment === null ? b : a}>
																		<WrapItem className="avatarWraper">
																			<Avatar
																				name={item?.viewer?.fullNameKhmer}
																				src={
																					item?.viewer?.avatar?.icon
																						? c.baseFilePath + item?.viewer?.avatar?.icon
																						: undefined
																				}
																			>
																				{item?.approveByDepartment === true ? (
																					<AvatarBadge boxSize="1.25em" bg="green.500">
																						<CheckIcon color="white" boxSize="0.55em" />
																					</AvatarBadge>
																				) : item?.approveByDepartment === false ? (
																					<AvatarBadge boxSize="1.25em" bg="red.500">
																						<CloseIcon color="white" boxSize="0.55em" />
																					</AvatarBadge>
																				) : (
																					<AvatarBadge boxSize="1.25em" bg="gray">
																						<QuestionIcon color="white" boxSize="12px" />
																					</AvatarBadge>
																				)}
																			</Avatar>
																		</WrapItem>
																	</Tooltip>
																	<Text textAlign="center" mt={1.5}>
																		{currentLang === 'en' ? item?.viewer?.fullName : item?.viewer?.fullNameKhmer || 'N/A'}
																	</Text>
																</Flex>
																<Flex>
																	{canActionViewer(item, 'Department') && showApproveButton && (
																		<SimpleGrid columns={2} spacingX={2} mt={2} ml={2}>
																			<Button
																				width="100%"
																				onClick={() => approveMissionRecord({ variables: { id } })}
																				leftIcon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 16 }} />}
																				colorScheme="green"
																				variant="solid"
																				size="sm"
																				borderRadius={c.borderRadius}
																				border="1px solid #bdc3c7"
																				isLoading={loadingApproveMissionRecord}
																			>
																				{t('mrApprove')}
																			</Button>

																			<Button
																				width="100%"
																				onClick={onOpen}
																				leftIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
																				colorScheme="red"
																				variant="solid"
																				size="sm"
																				borderRadius={c.borderRadius}
																				border="1px solid #bdc3c7"
																			>
																				{t('Reject')}
																			</Button>
																		</SimpleGrid>
																	)}
																</Flex>
															</>
														);
													})}
												</Wrap>
											</Flex>
										)}

										{record?.category === 'RequestForMeetingParticipation' && !isCreatedByDepartmentUser && (
											<Flex mt={4} pt={1} pb={1}>
												<Text fontWeight="bold" minW="200px" w="200px">
													{t('Approved by Division')}
												</Text>
												<Box>{showOfficeNABadge ? <Badge colorScheme="gray">{t('N/A')}</Badge> : null}</Box>
												<Wrap>
													{officeViewers
														?.sort((a, b) => {
															const aCanAction = canActionViewer(a, 'Office');
															const bCanAction = canActionViewer(b, 'Office');

															if (aCanAction && !bCanAction) return 1;
															if (!aCanAction && bCanAction) return -1;

															return 0;
														})
														.map((item, index) => {
															const status =
																item?.approveByOffice === true
																	? t('mrAPPROVED')
																	: item?.approveByOffice === false
																		? t('mrREJECTED')
																		: 'N/A';

															const approvedAt = item?.approveByOfficeAt ? moment(item?.approveByOfficeAt).format('LLLL') : null;

															const a = (
																<Box textAlign="center">
																	<UserDislayNameById id={item?.viewer?.id} />
																	{item?.viewer?.position?.length > 0 ? ', ' : ''}
																	{getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																	{', '}
																	{t(status)} {t('on')}
																	{approvedAt ? ` ${approvedAt}` : ''}
																	<br />
																	{item?.approveByOffice === false ? `${t('Reason')} : ${item?.rejectReason}` : null}
																</Box>
															);

															const b = (
																<Box textAlign="center">
																	<UserDislayNameById id={item?.viewer?.id} />
																	{', '} {getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																	{', '}
																	{t("Waiting for Division's approval")}
																</Box>
															);

															return (
																<>
																	<Flex direction="column" justifyContent="center" alignItems="center" key={'d-' + index}>
																		<Tooltip label={item?.approveByOffice === null ? b : a}>
																			<WrapItem className="avatarWraper">
																				<Avatar
																					name={item?.viewer?.fullNameKhmer}
																					src={
																						item?.viewer?.avatar?.icon
																							? c.baseFilePath + item?.viewer?.avatar?.icon
																							: undefined
																					}
																				>
																					{item?.approveByOffice === true ? (
																						<AvatarBadge boxSize="1.25em" bg="green.500">
																							<CheckIcon color="white" boxSize="0.55em" />
																						</AvatarBadge>
																					) : item?.approveByOffice === false ? (
																						<AvatarBadge boxSize="1.25em" bg="red.500">
																							<CloseIcon color="white" boxSize="0.55em" />
																						</AvatarBadge>
																					) : (
																						<AvatarBadge boxSize="1.25em" bg="gray">
																							<QuestionIcon color="white" boxSize="12px" />
																						</AvatarBadge>
																					)}
																				</Avatar>
																			</WrapItem>
																		</Tooltip>
																		<Text textAlign="center" mt={1.5}>
																			{currentLang === 'en'
																				? item?.viewer?.fullName
																				: item?.viewer?.fullNameKhmer || 'N/A'}
																		</Text>
																	</Flex>
																	<Flex>
																		{canActionViewer(item, 'Office') && showApproveButton && (
																			<SimpleGrid columns={2} spacingX={2} mt={2} ml={2}>
																				<Button
																					width="100%"
																					onClick={() => approveMissionRecord({ variables: { id } })}
																					leftIcon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 16 }} />}
																					colorScheme="green"
																					variant="solid"
																					size="sm"
																					borderRadius={c.borderRadius}
																					border="1px solid #bdc3c7"
																					isLoading={loadingApproveMissionRecord}
																				>
																					{t('mrApprove')}
																				</Button>

																				<Button
																					width="100%"
																					onClick={onOpen}
																					leftIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
																					colorScheme="red"
																					variant="solid"
																					size="sm"
																					borderRadius={c.borderRadius}
																					border="1px solid #bdc3c7"
																				>
																					{t('Reject')}
																				</Button>
																			</SimpleGrid>
																		)}
																	</Flex>
																</>
															);
														})}
												</Wrap>
											</Flex>
										)}

										{shouldShowParticipantSection && (
											<Flex mt={4} pt={1} pb={1}>
												<Text fontWeight="bold" minW="200px" w="200px">
													{t('Accepted by Participant')}
												</Text>

												<Box>{visibleParticipantViewers?.length === 0 ? <Badge colorScheme="gray">{t('N/A')}</Badge> : null}</Box>

												<Wrap>
													{visibleParticipantViewers
														?.sort((a, b) => {
															const aCanAction = canActionViewer(a, 'Participant');
															const bCanAction = canActionViewer(b, 'Participant');

															if (aCanAction && !bCanAction) return 1;
															if (!aCanAction && bCanAction) return -1;

															return 0;
														})
														.map((item, index) => {
															const status =
																item?.acceptByParticipant === true
																	? t('ACCEPTED')
																	: item?.acceptByParticipant === false
																		? t('REJECTED')
																		: 'N/A';

															const acceptedAt = item?.acceptByParticipantAt
																? moment(item?.acceptByParticipantAt).format('LLLL')
																: null;

															const a = (
																<Box textAlign="center">
																	<UserDislayNameById id={item?.viewer?.id} />
																	{item?.viewer?.position?.length > 0 ? ', ' : ''}
																	{getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																	{', '}
																	{t(status)} {t('on')}
																	{acceptedAt ? ` ${acceptedAt}` : ''}
																	<br />
																	{item?.acceptByParticipant === false ? `${t('Reason')} : ${item?.rejectReason}` : null}
																</Box>
															);

															const b = (
																<Box textAlign="center">
																	<UserDislayNameById id={item?.viewer?.id} />
																	{', '} {getPositionLabel(item?.viewer?.position, currentLang === 'kh' ? 'kh' : 'en')}
																	{', '}
																	{t("Waiting for Participant's Acceptance")}
																</Box>
															);

															return (
																<>
																	<Flex direction="column" justifyContent="center" alignItems="center">
																		<Tooltip label={item?.acceptByParticipant === null ? b : a}>
																			<WrapItem className="avatarWraper">
																				<Avatar
																					name={item?.viewer?.fullNameKhmer}
																					src={
																						item?.viewer?.avatar?.icon
																							? c.baseFilePath + item?.viewer?.avatar?.icon
																							: undefined
																					}
																				>
																					{item?.acceptByParticipant === true ? (
																						<AvatarBadge boxSize="1.25em" bg="green.500">
																							<CheckIcon color="white" boxSize="0.55em" />
																						</AvatarBadge>
																					) : item?.acceptByParticipant === false ? (
																						<AvatarBadge boxSize="1.25em" bg="red.500">
																							<CloseIcon color="white" boxSize="0.55em" />
																						</AvatarBadge>
																					) : (
																						<AvatarBadge boxSize="1.25em" bg="gray">
																							<QuestionIcon color="white" boxSize="12px" />
																						</AvatarBadge>
																					)}
																				</Avatar>

																				{canRemoveParticipant &&
																					!isCreatorRemovingSelf(item) &&
																					!isCurrentUserParticipantProfile(item) && (
																						<FontAwesomeIcon
																							onClick={() => {
																								setParticipantToRemove(item);
																								onRemoveUserModalOpen();
																							}}
																							className="remove"
																							icon={faMinusSquare}
																							style={{
																								fontSize: 18,
																								display: 'none',
																								position: 'absolute',
																								zIndex: '100',
																								marginLeft: 30,
																								marginBottom: 30,
																								color: 'red',
																								backgroundColor: 'white',
																								padding: 2,
																								cursor: 'pointer',
																							}}
																						/>
																					)}
																			</WrapItem>
																		</Tooltip>

																		<Text textAlign="center" mt={1.5}>
																			{currentLang === 'en'
																				? item?.viewer?.fullName
																				: item?.viewer?.fullNameKhmer || 'N/A'}
																		</Text>
																	</Flex>

																	<Flex>
																		{canActionViewer(item, 'Participant') && (
																			<SimpleGrid columns={2} spacingX={2} mt={2} ml={2}>
																				<Button
																					width="100%"
																					onClick={() => approveMissionRecord({ variables: { id } })}
																					leftIcon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 16 }} />}
																					colorScheme="green"
																					variant="solid"
																					size="sm"
																					borderRadius={c.borderRadius}
																					border="1px solid #bdc3c7"
																					isLoading={loadingApproveMissionRecord}
																				>
																					{t('Accept')}
																				</Button>

																				<Button
																					width="100%"
																					onClick={onOpen}
																					leftIcon={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
																					colorScheme="red"
																					variant="solid"
																					size="sm"
																					borderRadius={c.borderRadius}
																					border="1px solid #bdc3c7"
																				>
																					{t('Reject')}
																				</Button>
																			</SimpleGrid>
																		)}
																	</Flex>
																</>
															);
														})}
												</Wrap>
												{canManageParticipant && (
													<Flex>
														<Button
															className="hidden-print"
															size="sm"
															colorScheme="blue"
															variant="outline"
															leftIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 18 }} />}
															borderRadius={c.borderRadius}
															onClick={() => setOpenSelectUserAllModel('viewer')}
															isLoading={loadingAddParticipant}
															mt={2}
															ml={4}
														>
															{t('Add')}
														</Button>
													</Flex>
												)}
											</Flex>
										)}
									</Box>
								</Box>
							</Box>
						</Center>
						<Box className="hidden-print">
							{data?.getMissionRecordById?.id && <ViewComment id={data?.getMissionRecordById?.id} type="MISSION_RECORD" />}
						</Box>
					</Container>
				</>

				<AlertDialog isOpen={isAttachmentModalOpen} onClose={onAttachmentModalClose}>
					<AlertDialogOverlay>
						<AlertDialogContent>
							<AlertDialogHeader fontSize="lg" fontWeight="bold">
								{t('Remove Attachment')}
							</AlertDialogHeader>
							<AlertDialogBody>
								<Text fontSize="15px">{t('Are you sure you want to remove this attachment? This action cannot be undone.')}</Text>
							</AlertDialogBody>
							<AlertDialogFooter>
								<Button ref={cancelRef} onClick={onAttachmentModalClose}>
									{t('Cancel')}
								</Button>
								<Button
									colorScheme="red"
									onClick={async () => {
										await removeAttachment({ variables: { id } });

										toast({
											title: t('Attachment has been removed successfully'),
											status: 'success',
											isClosable: true,
											position: 'top-right',
											duration: 3000,
										});

										onAttachmentModalClose();
										getDetail();
									}}
									ml={3}
								>
									{t('Remove')}
								</Button>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialogOverlay>
				</AlertDialog>

				{openSelectUserAllModel && (
					<SelectUserAllModal
						cached={false}
						onClose={() => setOpenSelectUserAllModel(false)}
						callback={async (e) => {
							if (openSelectUserAllModel === 'viewer') {
								if (!canManageParticipant) {
									toast({
										title: t('You are not allowed to manage participant for this record'),
										status: 'error',
										isClosable: true,
										position: 'top-right',
										duration: 3000,
									});
									setOpenSelectUserAllModel(false);
									return;
								}

								const existed = record?.viewers?.some((item) => {
									return String(item?.viewer?.id) === String(e?.id) && isParticipantViewer(item);
								});

								const isDeputyHead = e?.position === '4' && e?.canApproveMissionRecord === true;

								if (existed) {
									toast({
										title: t('Participant already added'),
										status: 'warning',
										isClosable: true,
										position: 'top-right',
										duration: 3000,
									});

									setOpenSelectUserAllModel(false);
									return;
								}

								if (e?.position === '1' || e?.position === '2' || e?.position === '3' || isDeputyHead) {
									toast({
										title:
											e?.position === '1'
												? t('DG account cannot be added as participant')
												: t('Department account cannot be added as participant'),
										status: 'error',
										isClosable: true,
										duration: 3000,
										position: 'top-right',
									});

									setOpenSelectUserAllModel(false);
									return;
								}

								const currentUserDepartment = user?.department;
								const selectedUserDepartment = e?.department;

								if (currentUserDepartment && selectedUserDepartment && currentUserDepartment !== selectedUserDepartment) {
									toast({
										title: t('Cannot add participant from another department'),
										status: 'error',
										isClosable: true,
										duration: 3000,
										position: 'top-right',
									});

									setOpenSelectUserAllModel(false);
									return;
								}

								try {
									await addParticipant({
										variables: {
											id,
											participantId: e?.id,
										},
									});

									toast({
										title: t('You have added a participant'),
										status: 'success',
										isClosable: true,
										position: 'top-right',
										duration: 3000,
									});

									getDetail();
								} catch (error) {
									toast({
										title: t(error?.message || 'something went wrong'),
										status: 'error',
										isClosable: true,
										position: 'top-right',
										duration: 3000,
									});
								}
							}

							setOpenSelectUserAllModel(false);
						}}
						viewType="all"
					/>
				)}

				<AlertDialog isOpen={isRemoveUserModalOpen} onClose={onRemoveUserModalClose}>
					<AlertDialogOverlay>
						<AlertDialogContent>
							<AlertDialogHeader fontSize="lg" fontWeight="bold">
								{t('Remove User')}
							</AlertDialogHeader>
							<AlertDialogBody>
								<Text fontSize="15px">{t('Are you sure you want to remove this user? This action cannot be undone.')}</Text>
							</AlertDialogBody>
							<AlertDialogFooter>
								<Button ref={cancelRef} onClick={onRemoveUserModalClose}>
									{t('Cancel')}
								</Button>
								<Button
									colorScheme="red"
									isLoading={loadingRemoveParticipant}
									onClick={async () => {
										if (participantViewers?.length <= 1) {
											toast({
												title: t('At least one participant is required'),
												status: 'error',
												isClosable: true,
												position: 'top-right',
												duration: 3000,
											});
											return;
										}

										if (!participantToRemove?.viewer?.id) {
											toast({
												title: t('Participant not found'),
												status: 'error',
												isClosable: true,
												position: 'top-right',
												duration: 3000,
											});
											return;
										}

										// if (participantToRemove?.viewer?.id === record?.createdBy) {
										// 	toast({
										// 		title: t('Creator cannot be removed'),
										// 		status: 'warning',
										// 		isClosable: true,
										// 		position: 'top-right',
										// 		duration: 3000,
										// 	});

										// 	setParticipantToRemove(null);
										// 	onRemoveUserModalClose();

										// 	return;
										// }

										await removeParticipant({
											variables: {
												id,
												participantId: participantToRemove?.viewer?.id,
											},
										});

										toast({
											title: t('Participant has been removed successfully'),
											status: 'success',
											isClosable: true,
											position: 'top-right',
											duration: 3000,
										});

										setParticipantToRemove(null);
										onRemoveUserModalClose();
										getDetail();
									}}
									ml={3}
								>
									{t('Remove')}
								</Button>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialogOverlay>
				</AlertDialog>

				<>
					<Modal isOpen={isOpen} onClose={onClose}>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>{t('Reason')}</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								<Textarea onChange={(e) => setRejectReason(e?.currentTarget?.value)} />
							</ModalBody>

							<ModalFooter>
								<Button
									colorScheme="red"
									onClick={() => {
										if (!rejectReason) {
											alert('Please input reject reason');
										} else {
											rejectMissionRecord({ variables: { id, reason: rejectReason } });
										}
									}}
									isLoading={loadingRejectMissionRecord}
								>
									{t('Reject Leave')}
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>
				</>
			</PrintLayout>
		</>
	);
}

export default MissionRecordDetail;
